import { supabase } from './supabase';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  retry?: number; // number of retries
  retryDelayMs?: number; // delay between retries
}

interface JsonResponse<T> {
  ok: boolean;
  status: number;
  data?: T;
  errorText?: string;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

function buildHeaders(base?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(base || {}),
  };
  return headers;
}

async function getAuthHeader(): Promise<string | undefined> {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) return `Bearer ${session.access_token}`;
  if (SUPABASE_ANON_KEY) return `Bearer ${SUPABASE_ANON_KEY}`;
  return undefined;
}

export async function requestJson<T = any>(url: string, options: RequestOptions = {}): Promise<JsonResponse<T>> {
  const { method = 'GET', headers = {}, body, retry = 0, retryDelayMs = 500 } = options;

  const finalHeaders = buildHeaders(headers);
  // Conditionally include apikey only for Supabase endpoints
  try {
    const u = new URL(url);
    const isSupabase = (SUPABASE_URL && url.startsWith(SUPABASE_URL)) || u.hostname.endsWith('.supabase.co');
    if (isSupabase && SUPABASE_ANON_KEY && !Object.keys(finalHeaders).some(k => k.toLowerCase() === 'apikey')) {
      finalHeaders['apikey'] = SUPABASE_ANON_KEY;
    }
  } catch {
    // ignore URL parsing issues; best-effort only
  }
  // Respect a caller-provided Authorization header
  const hasCallerAuth = Object.keys(finalHeaders).some(k => k.toLowerCase() === 'authorization');
  if (!hasCallerAuth) {
    const authHeader = await getAuthHeader();
    if (authHeader) finalHeaders['Authorization'] = authHeader;
  }

  const doFetch = async (): Promise<Response> => {
    return fetch(url, {
      method,
      headers: finalHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  let attempt = 0;
  let res: Response | null = null;
  let lastErr: any = null;

  while (attempt <= retry) {
    try {
      res = await doFetch();
      break;
    } catch (err) {
      lastErr = err;
      if (attempt === retry) break;
      await new Promise(r => setTimeout(r, retryDelayMs));
      attempt += 1;
    }
  }

  if (!res) {
    return { ok: false, status: 0, errorText: String(lastErr || 'Network error') };
  }

  const status = res.status;
  if (!res.ok) {
    const errorText = await res.text().catch(() => '');
    return { ok: false, status, errorText };
  }

  // Try parse JSON, fallback to text
  try {
    const data = (await res.json()) as T;
    return { ok: true, status, data };
  } catch {
    const text = await res.text();
    return { ok: true, status, data: text as unknown as T };
  }
}

// Edge Function helper using the above
export async function callEdge<TReq = any, TRes = any>(functionName: string, payload: TReq, opts?: { retry?: number; retryDelayMs?: number; baseUrlOverride?: string; }): Promise<TRes> {
  const base = opts?.baseUrlOverride || (SUPABASE_URL ? `${SUPABASE_URL}/functions/v1/` : '');
  const url = `${base}${functionName}`;

  console.log(`📡 [API] Appel Edge Function: ${functionName}`, payload);
  const startTime = Date.now();

  const res = await requestJson<TRes>(url, { method: 'POST', body: payload, retry: opts?.retry ?? 0, retryDelayMs: opts?.retryDelayMs ?? 500 });

  const duration = Date.now() - startTime;

  if (!res.ok) {
    console.error(`❌ [API] Échec ${functionName} (${res.status}) après ${duration}ms:`, res.errorText);
    throw new Error(`Edge call failed (${res.status}): ${res.errorText || 'Unknown error'}`);
  }

  console.log(`✅ [API] Succès ${functionName} en ${duration}ms`);
  return res.data as TRes;
}
